import React, { useEffect, useState, useCallback } from 'react';
import { Col, Container, Row, Button, Modal, Form, Table, Alert } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import HeaderAdmin from './HeaderAdmin';
import { firestore } from '../Firebase';

const RegisteredUsers = () => {
  const [users, setUsers] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: 'createdAt',
    direction: 'desc',
  });

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editFormData, setEditFormData] = useState({
    userName: '',
    firstName: '',
    lastName: '',
    email: '',
    location: '',
  });

  useEffect(() => {
    const unsubscribeUsers = firestore
      .collection('users')
      .onSnapshot((snapshot) => {
        const registeredUsers = snapshot.docs.map((doc) => {
          const userData = doc.data();

          // Format created at time if it exists
          let formattedCreatedAt = 'N/A';
          let createdAtTimestamp = 0;
          if (userData.createdAt) {
            if (userData.createdAt instanceof Object && userData.createdAt.seconds) {
              createdAtTimestamp = userData.createdAt.seconds * 1000;
              formattedCreatedAt = new Date(createdAtTimestamp).toLocaleString();
            } else if (typeof userData.createdAt === 'string') {
                // Handle if it's stored as ISO string or similar
                const date = new Date(userData.createdAt);
                if (!isNaN(date.getTime())) {
                    createdAtTimestamp = date.getTime();
                    formattedCreatedAt = date.toLocaleString();
                }
            }
          }

          return {
            id: doc.id,
            ...userData,
            formattedCreatedAt,
            createdAtTimestamp,
          };
        });

        setUsers(registeredUsers);
      });

    return () => unsubscribeUsers();
  }, []);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedUsers = React.useMemo(() => {
    let sortableUsers = [...users];
    if (sortConfig.key !== null) {
      sortableUsers.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle special cases for sorting
        if (sortConfig.key === 'createdAt') {
          aValue = a.createdAtTimestamp;
          bValue = b.createdAtTimestamp;
        }

        // Handle string comparison case-insensitively
        if (typeof aValue === 'string' && typeof bValue === 'string') {
            aValue = aValue.toLowerCase();
            bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableUsers;
  }, [users, sortConfig]);

  const downloadCSV = () => {
    const headers = [
      'User Name',
      'First Name',
      'Last Name',
      'Email',
      'Location',
      'Role',
      'Created At',
      'User ID',
    ];

    let csvContent = headers.join(',') + '\n';

    sortedUsers.forEach((user) => {
      const row = [
        user.userName || '',
        user.firstName || '',
        user.lastName || '',
        user.email || '',
        user.location || '',
                user.role || '',
                user.formattedCreatedAt || '',
                user.userID || user.id || '',
              ];

      const escapedRow = row.map((field) => {
        if (field && (field.includes(',') || field.includes('"'))) {
          return `"${field.replace(/"/g, '""')}"`;
        }
        return field;
      });

      csvContent += escapedRow.join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `registered_users_${new Date().toISOString().slice(0, 10)}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
    }
    return null;
  };

  const deleteUser = async (userId) => {
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this user? This action cannot be undone.'
    );

    if (confirmDelete) {
      try {
        await firestore.collection('users').doc(userId).delete();
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Error deleting user: ' + error.message);
      }
    }
  };

  const deleteAllUsers = async () => {
    const confirmDelete = window.confirm(
      'Are you sure you want to delete ALL users? This action cannot be undone.'
    );

    if (confirmDelete) {
      try {
        const snapshot = await firestore.collection('users').get();
        const batchSize = 500;
        
        if (snapshot.size === 0) {
            alert("No users to delete.");
            return;
        }

        // We need to loop because batch limit is 500
        for (let i = 0; i < snapshot.docs.length; i += batchSize) {
            const batch = firestore.batch();
            const chunk = snapshot.docs.slice(i, i + batchSize);
            
            chunk.forEach(doc => {
                batch.delete(doc.ref);
            });
            
            await batch.commit();
        }
        
        alert('All users have been deleted.');
      } catch (error) {
        console.error('Error deleting all users:', error);
        alert('Error deleting all users: ' + error.message);
      }
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditFormData({
      userName: user.userName || '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      location: user.location || '',
    });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingUser(null);
    setEditFormData({
      userName: '',
      firstName: '',
      lastName: '',
      email: '',
      location: '',
    });
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;

    try {
      await firestore.collection('users').doc(editingUser.id).update({
        userName: editFormData.userName,
        firstName: editFormData.firstName,
        lastName: editFormData.lastName,
        email: editFormData.email,
        location: editFormData.location,
      });
      handleCloseEditModal();
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Error updating user: ' + error.message);
    }
  };

  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [addFormData, setAddFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    userName: '',
    location: '',
  });
  const [uploadStep, setUploadStep] = useState('upload'); // 'upload', 'preview', 'processing', 'summary'
  const [importStats, setImportStats] = useState({
    total: 0,
    success: 0,
    failed: 0,
    duplicates: 0,
    errors: [],
  });

  // State for valid users to be imported after duplicate checking
  const [validUsersToImport, setValidUsersToImport] = useState([]);
  const [duplicateUsers, setDuplicateUsers] = useState([]);
  const [invalidUsers, setInvalidUsers] = useState([]);

  // Ref for the email input in the add user modal
  const addEmailInputRef = React.useRef(null);

  // Focus the email input when the add modal is shown
  useEffect(() => {
    if (showAddModal && addEmailInputRef.current) {
      addEmailInputRef.current.focus();
    }
  }, [showAddModal]);

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const processCSV = useCallback(
    (text) => {
      const lines = text.split(/\r\n|\n/);
      const headers = lines[0]
        .split(',')
        .map((header) => header.trim().toLowerCase().replace(/"/g, ''));

      const requiredIndex = headers.indexOf('email');
      
      // Map other optional fields
      const firstNameIndex = headers.findIndex(h => h.includes('first') && h.includes('name'));
      const lastNameIndex = headers.findIndex(h => h.includes('last') && h.includes('name'));
      const userNameIndex = headers.findIndex(h => h === 'user name' || h === 'username');
      const locationIndex = headers.findIndex(h => h === 'location');

      if (requiredIndex === -1) {
        setImportStats((prev) => ({
          ...prev,
          errors: ['CSV must contain an "Email" column.'],
        }));
        setUploadStep('summary');
        return;
      }

      const parsedUsers = [];
      const duplicates = [];
      const invalid = [];

      // Create a set of existing emails for O(1) lookup
      const existingEmails = new Set(users.map((u) => u.email?.toLowerCase()));
      // Also track emails in the current CSV to prevent duplicates within the file
      const currentCsvEmails = new Set();

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        // Parse CSV line character by character to handle quotes and spaces correctly
        const currentData = [];
        let currentToken = '';
        let inQuotes = false;
        
        for (let j = 0; j < line.length; j++) {
            const char = line[j];
            if (inQuotes) {
                if (char === '"') {
                    if (j + 1 < line.length && line[j + 1] === '"') {
                        currentToken += '"';
                        j++;
                    } else {
                        inQuotes = false;
                    }
                } else {
                    currentToken += char;
                }
            } else {
                if (char === '"') {
                    inQuotes = true;
                } else if (char === ',') {
                    currentData.push(currentToken.trim());
                    currentToken = '';
                } else {
                    currentToken += char;
                }
            }
        }
        currentData.push(currentToken.trim());

        const email = currentData[requiredIndex];

        if (!email || !isValidEmail(email)) {
          invalid.push({ row: i + 1, reason: 'Invalid or missing email', data: line });
          continue;
        }

        const lowerEmail = email.toLowerCase();

        if (existingEmails.has(lowerEmail) || currentCsvEmails.has(lowerEmail)) {
          duplicates.push({ row: i + 1, email: email });
          continue;
        }

        currentCsvEmails.add(lowerEmail);

        parsedUsers.push({
          email: email,
          firstName: firstNameIndex !== -1 ? currentData[firstNameIndex] || '' : '',
          lastName: lastNameIndex !== -1 ? currentData[lastNameIndex] || '' : '',
          userName: userNameIndex !== -1 ? currentData[userNameIndex] || '' : '',
          location: locationIndex !== -1 ? currentData[locationIndex] || '' : '',
        });
      }

      setValidUsersToImport(parsedUsers);
      setDuplicateUsers(duplicates);
      setInvalidUsers(invalid);
      setUploadStep('preview');
    },
    [users]
  );

  const onDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      const reader = new FileReader();

      reader.onabort = () => console.log('file reading was aborted');
      reader.onerror = () => console.log('file reading has failed');
      reader.onload = () => {
        const binaryStr = reader.result;
        processCSV(binaryStr);
      };
      reader.readAsText(file);
    },
    [processCSV]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
    },
    maxFiles: 1,
  });

  const handleAddFormChange = (e) => {
    const { name, value } = e.target;
    setAddFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setAddFormData({
      email: '',
      firstName: '',
      lastName: '',
      userName: '',
      location: '',
    });
  };

  const handleAddUser = async () => {
    if (!addFormData.email) {
      alert('Email is required');
      return;
    }

    if (!isValidEmail(addFormData.email)) {
      alert('Please enter a valid email address');
      return;
    }

    try {
      // Check for duplicates
      const duplicateCheck = await firestore
        .collection('users')
        .where('email', '==', addFormData.email)
        .get();

      if (!duplicateCheck.empty) {
        alert('A user with this email already exists.');
        return;
      }

      const docRef = firestore.collection('users').doc();
      await docRef.set({
        ...addFormData,
        role: 'viewer',
        createdAt: new Date(),
        userID: docRef.id,
        loggedIn: false,
      });

      handleCloseAddModal();
    } catch (error) {
      console.error('Error adding user:', error);
      alert('Error adding user: ' + error.message);
    }
  };

  const handleAddSubmit = (e) => {
    e.preventDefault();
    handleAddUser();
  };

  const handleCloseBulkModal = () => {
    setShowBulkModal(false);
    setUploadStep('upload');
    setValidUsersToImport([]);
    setDuplicateUsers([]);
    setInvalidUsers([]);
    setImportStats({
      total: 0,
      success: 0,
      failed: 0,
      duplicates: 0,
      errors: [],
    });
  };

  const handleBulkImport = async () => {
    setUploadStep('processing');
    const batchSize = 500;
    const stats = {
      total: validUsersToImport.length,
      success: 0,
      failed: 0,
      duplicates: duplicateUsers.length,
      errors: [],
    };

    try {
      // Split into chunks of 500 for batch writing
      for (let i = 0; i < validUsersToImport.length; i += batchSize) {
        const batch = firestore.batch();
        const chunk = validUsersToImport.slice(i, i + batchSize);

        chunk.forEach((user) => {
          const docRef = firestore.collection('users').doc();
          batch.set(docRef, {
            ...user,
            role: 'viewer',
            createdAt: new Date(),
            userID: docRef.id,
            loggedIn: false,
          });
        });

        await batch.commit();
        stats.success += chunk.length;
      }
    } catch (error) {
      console.error('Batch import failed:', error);
      stats.failed = validUsersToImport.length - stats.success;
      stats.errors.push(error.message);
    }

    setImportStats(stats);
    setUploadStep('summary');
  };

  const renderBulkContent = () => {
    if (uploadStep === 'upload') {
      return (
        <div>
          <div
            {...getRootProps()}
            className={`p-5 text-center border rounded ${
              isDragActive ? 'border-primary bg-light' : 'border-secondary'
            }`}
            style={{ cursor: 'pointer', borderStyle: 'dashed' }}
          >
            <input {...getInputProps()} />
            {isDragActive ? (
              <p>Drop the CSV file here ...</p>
            ) : (
              <div>
                <p>Drag 'n' drop a CSV file here, or click to select one</p>
              </div>
            )}
          </div>
          <div className="mt-3">
            <small className="text-muted d-block mb-1">
              Copy and paste this header row into your CSV:
            </small>
            <code className="p-2 bg-light border rounded d-block text-break user-select-all">
              Email,First Name,Last Name,User Name,Location
            </code>
          </div>
        </div>
      );
    }

    if (uploadStep === 'preview') {
      return (
        <div>
          <Alert variant="info">
            <p className="mb-1"><strong>Ready to Import:</strong> {validUsersToImport.length}</p>
            <p className="mb-1"><strong>Duplicates (Skipped):</strong> {duplicateUsers.length}</p>
            <p className="mb-0"><strong>Invalid (Skipped):</strong> {invalidUsers.length}</p>
          </Alert>

          {invalidUsers.length > 0 && (
             <div className="mb-3">
                <strong>Invalid Rows:</strong>
                <ul className="text-danger small">
                    {invalidUsers.slice(0, 5).map((err, i) => (
                        <li key={i}>Row {err.row}: {err.reason}</li>
                    ))}
                    {invalidUsers.length > 5 && <li>...and {invalidUsers.length - 5} more</li>}
                </ul>
             </div>
          )}

          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>User Name</th>
                  <th>Location</th>
                </tr>
              </thead>
              <tbody>
                {validUsersToImport.slice(0, 100).map((user, index) => (
                  <tr key={index}>
                    <td>{user.email}</td>
                    <td>{user.firstName}</td>
                    <td>{user.lastName}</td>
                    <td>{user.userName}</td>
                    <td>{user.location}</td>
                  </tr>
                ))}
                {validUsersToImport.length > 100 && (
                    <tr>
                        <td colSpan="5" className="text-center text-muted">
                            ... and {validUsersToImport.length - 100} more rows
                        </td>
                    </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      );
    }

    if (uploadStep === 'processing') {
        return (
            <div className="text-center p-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Processing import... Please wait.</p>
            </div>
        );
    }

    if (uploadStep === 'summary') {
      return (
        <div className="text-center">
          {importStats.errors.length > 0 ? (
             <Alert variant="danger">
                <h4>Import Failed</h4>
                <ul>
                    {importStats.errors.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
             </Alert>
          ) : (
              <Alert variant="success">
                <h4>Import Complete</h4>
                <p>Successfully imported {importStats.success} users.</p>
                <p>Skipped {importStats.duplicates} duplicates.</p>
              </Alert>
          )}
        </div>
      );
    }
  };

  return (
    <>
      <Container fluid>
        <Row>
          <HeaderAdmin />
        </Row>

        <Modal show={showEditModal} onHide={handleCloseEditModal}>
          <Form onSubmit={(e) => { e.preventDefault(); handleSaveUser(); }}>
            <Modal.Header closeButton>
              <Modal.Title>Edit User</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>User Name</Form.Label>
                <Form.Control
                  type="text"
                  name="userName"
                  value={editFormData.userName}
                  onChange={handleEditFormChange}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  type="text"
                  name="firstName"
                  value={editFormData.firstName}
                  onChange={handleEditFormChange}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  type="text"
                  name="lastName"
                  value={editFormData.lastName}
                  onChange={handleEditFormChange}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={editFormData.email}
                  onChange={handleEditFormChange}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Location</Form.Label>
                <Form.Control
                  type="text"
                  name="location"
                  value={editFormData.location}
                  onChange={handleEditFormChange}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseEditModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Save Changes
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Modal show={showAddModal} onHide={handleCloseAddModal}>
          <Form onSubmit={handleAddSubmit}>
            <Modal.Header closeButton>
              <Modal.Title>Add User</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Email *</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={addFormData.email}
                  onChange={handleAddFormChange}
                  ref={addEmailInputRef}
                  autoFocus
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>User Name</Form.Label>
                <Form.Control
                  type="text"
                  name="userName"
                  value={addFormData.userName}
                  onChange={handleAddFormChange}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  type="text"
                  name="firstName"
                  value={addFormData.firstName}
                  onChange={handleAddFormChange}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  type="text"
                  name="lastName"
                  value={addFormData.lastName}
                  onChange={handleAddFormChange}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Location</Form.Label>
                <Form.Control
                  type="text"
                  name="location"
                  value={addFormData.location}
                  onChange={handleAddFormChange}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseAddModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Add User
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Modal show={showBulkModal} onHide={handleCloseBulkModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Bulk User Upload (CSV)</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {renderBulkContent()}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseBulkModal}>
              {uploadStep === 'summary' ? 'Close' : 'Cancel'}
            </Button>
            {uploadStep === 'preview' && validUsersToImport.length > 0 && (
              <Button variant="primary" onClick={handleBulkImport}>
                Import {validUsersToImport.length} Users
              </Button>
            )}
          </Modal.Footer>
        </Modal>

        <Row>
          <Col>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="h3 m1-1 mb-0">Registered Users:</div>
              <div>
                <Button
                  variant="info"
                  size="sm"
                  className="me-2 text-white"
                  onClick={() => setShowBulkModal(true)}
                >
                  <i className="fas fa-file-upload mr-1"></i> Upload CSV
                </Button>
                <Button
                  variant="success"
                  size="sm"
                  className="me-2"
                  onClick={() => setShowAddModal(true)}
                >
                  <i className="fas fa-plus mr-1"></i> Add User
                </Button>
                <Button variant="primary" size="sm" className="me-2" onClick={downloadCSV}>
                  <i className="fas fa-download mr-1"></i> Download CSV
                </Button>
                <Button variant="danger" size="sm" onClick={deleteAllUsers}>
                  <i className="fas fa-trash-alt mr-1"></i> Delete All Users
                </Button>
              </div>
            </div>
            <table className="table table-striped" style={{ fontSize: '0.75rem' }}>
              <thead>
                <tr style={{ lineHeight: '1.2' }}>
                  <th
                    scope="col"
                    className="py-1 px-1"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('userName')}
                  >
                    User Name{getSortIcon('userName')}
                  </th>
                  <th
                    scope="col"
                    className="py-1 px-1"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('firstName')}
                  >
                    First Name{getSortIcon('firstName')}
                  </th>
                  <th
                    scope="col"
                    className="py-1 px-1"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('lastName')}
                  >
                    Last Name{getSortIcon('lastName')}
                  </th>
                  <th
                    scope="col"
                    className="py-1 px-1"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('email')}
                  >
                    Email{getSortIcon('email')}
                  </th>
                  <th
                    scope="col"
                    className="py-1 px-1"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('location')}
                  >
                    Location{getSortIcon('location')}
                  </th>
                  <th
                    scope="col"
                    className="py-1 px-1"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('role')}
                  >
                    Role{getSortIcon('role')}
                  </th>
                  <th
                    scope="col"
                    className="py-1 px-1"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('createdAt')}
                  >
                    Created At{getSortIcon('createdAt')}
                  </th>
                  <th
                    scope="col"
                    className="py-1 px-1"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('id')}
                  >
                    User ID{getSortIcon('id')}
                  </th>
                  <th scope="col" className="py-1 px-1">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((user) => (
                  <tr key={user.id} style={{ lineHeight: '1.2' }}>
                    <td className="py-1 px-1">{user.userName}</td>
                    <td className="py-1 px-1">{user.firstName}</td>
                    <td className="py-1 px-1">{user.lastName}</td>
                    <td className="py-1 px-1">{user.email}</td>
                    <td className="py-1 px-1">{user.location}</td>
                    <td className="py-1 px-1">{user.role}</td>
                    <td className="py-1 px-1">{user.formattedCreatedAt}</td>
                    <td className="py-1 px-1">{user.userID || user.id}</td>
                    <td className="py-1 px-1">
                      <Button
                        variant="primary"
                        size="sm"
                        className="me-1"
                        onClick={() => handleEditUser(user)}
                        style={{ padding: '0px 5px', fontSize: '0.7rem' }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => deleteUser(user.id)}
                        style={{ padding: '0px 5px', fontSize: '0.7rem' }}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default RegisteredUsers;
